import Link from "next/link";
import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "dark" | "light" | "violet" | "outline-dark";
type Size = "default" | "sm";

const base =
  "relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-full font-extrabold whitespace-nowrap tracking-[-0.01em] transition-transform duration-200 ease-[cubic-bezier(.19,1,.22,1)] hover:-translate-y-0.5";

const sizes: Record<Size, string> = {
  // Phones: every main button is the same height and fills the row, so
  // stacked buttons line up. From the sm breakpoint they size to their text.
  // Fixed heights (not padding-based) so a button never grows when its label
  // wraps or has an icon, and every button of a size is exactly as tall.
  default:
    "h-[52px] w-full px-6 text-[0.95rem] sm:w-auto sm:px-7 sm:text-[0.96rem]",
  sm: "h-10 px-5 text-[0.85rem]",
};

const variants: Record<Variant, string> = {
  dark: "bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep text-white shadow-[0_18px_38px_-14px_rgba(143,103,255,0.85),inset_0_1px_0_rgba(255,255,255,0.3)] hover:shadow-[0_22px_44px_-14px_rgba(143,103,255,0.95),inset_0_1px_0_rgba(255,255,255,0.35)]",
  light:
    "bg-gradient-to-b from-white to-violet-pale text-violet-deep border-[1.5px] border-violet-light shadow-[0_8px_22px_-14px_rgba(143,103,255,0.35)] hover:shadow-[0_18px_36px_-16px_rgba(143,103,255,0.5)]",
  violet:
    "bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep text-white shadow-[0_18px_38px_-14px_rgba(143,103,255,0.85),inset_0_1px_0_rgba(255,255,255,0.3)] hover:shadow-[0_22px_44px_-14px_rgba(143,103,255,0.95),inset_0_1px_0_rgba(255,255,255,0.35)]",
  "outline-dark":
    "bg-transparent text-violet-deep border-[1.5px] border-violet hover:bg-gradient-to-br hover:from-[#A488FF] hover:via-violet hover:to-violet-deep hover:text-white hover:border-transparent",
};

const grainStyle = {
  backgroundImage:
    "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
};

interface CommonProps {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
}

interface LinkButtonProps extends CommonProps {
  href: string;
}

interface NativeButtonProps
  extends
    CommonProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children"> {
  href?: undefined;
}

function Sheen({ variant }: { variant: Variant }) {
  const showGrain = variant !== "light" && variant !== "outline-dark";
  return (
    <>
      {showGrain && (
        <span
          className="pointer-events-none absolute inset-0 z-0 opacity-[0.06] mix-blend-overlay"
          style={grainStyle}
          aria-hidden
        />
      )}
      <span
        className="pointer-events-none absolute inset-y-0 left-[-60%] z-[1] w-[45%] -skew-x-[18deg] bg-gradient-to-r from-transparent via-white/45 to-transparent transition-[left] duration-700 ease-[cubic-bezier(.19,1,.22,1)] group-hover:left-[130%]"
        aria-hidden
      />
    </>
  );
}

export function Button(props: LinkButtonProps | NativeButtonProps) {
  const {
    variant = "dark",
    size = "default",
    className = "",
    children,
  } = props;
  const classes = `${base} group ${sizes[size]} ${variants[variant]} ${className}`;

  if ("href" in props && props.href) {
    return (
      <Link href={props.href} className={classes}>
        <Sheen variant={variant} />
        <span className="relative z-[2] inline-flex items-center justify-center gap-2 whitespace-nowrap">
          {children}
        </span>
      </Link>
    );
  }

  const {
    href: _href,
    variant: _variant,
    size: _size,
    className: _className,
    children: _children,
    ...rest
  } = props as NativeButtonProps;
  return (
    <button className={classes} {...rest}>
      <Sheen variant={variant} />
      <span className="relative z-[2] inline-flex items-center justify-center gap-2 whitespace-nowrap">
        {children}
      </span>
    </button>
  );
}
